/* Copyright (c) 2016-2023 Planticle Pty Ltd */

import ko from "knockout";
import birdata from "../../../api.js";
import fieldManager from "../../../util/fields.js";
import util from "../../../util/util.js";
import programs from '../../../programs.js';

function ManageEventsEditViewModel(params) {
	var self = this;

	var isNew = params.id === undefined;

	self.title = isNew ? "Create New Event" :  "Edit Event";
	self.submitting = ko.observable(false);
	self.submitButtonText = ko.pureComputed(function() {
		if(self.submitting()) {
			return "Submitting…";
		} else {
			return isNew ? "Create Event" : "Update Event";
		}
	});
	self.fields = ko.observable(null);
	self.fieldList = ko.observable(null);
	self.today = ko.observable(new Date().toISOString().substr(0, 10));
	self.submitError = ko.observable(null);
	self.loaded = ko.observable(false);

	function managedPrograms(user, programs) {
		if(user.is_administrator) {
			return programs;
		}

		var codes = user.permissions
			.filter(p => p.type == "program" && p.role == "admin")
			.map(p => p.program);
		return programs.filter(p => codes.includes(p.code));
	}

	function existingEventPromise(id) {
		if(id) {
			return birdata.event(id);
		} else {
			return Promise.resolve(null);
		}
	}

	Promise.all([
		birdata.lookup(),
		birdata.me(),
		existingEventPromise(params.id)])
	.then(function([lookup, me, event]) {
		var programs = managedPrograms(me, lookup.program);

		var fieldDefinitions = [{
			name: 'name',
			label: 'Event Name',
			type: 'text',
			validate: 'required',
			help: 'If this is a recurring event, consider including the year/month in the event name to distinguish it from future events.'
		}, {
			name: 'program',
			type: 'options',
			options: programs,
			validate: 'required'
		}, {
			name: 'startDate',
			help: 'First day event can be booked for'
		}, {
			name: 'finishDate',
			help: 'Last day event can be booked for'
		}, {
			name: 'bookingsOpen',
			type: 'boolean'
		}, {
			name: 'eventInfoURL',
			label: 'Event information URL',
			type: 'text',
			help: 'Optional - specify a page with further information about this event. A link to this URL will be shown on the Birdata event page for this event.'
		}, {
			name: 'maxBookingsPerPersonPerDay',
			label: 'Maximum bookings per person per day',
			type: 'integer',
			validate: '>0,<99',
			units: 'bookings'
		}, {
			name: 'maxBookingsPerSite',
			label: 'Maximum bookings per site per event period',
			type: 'integer',
			validate: '>0',
			units: 'bookings'
		}, {
			name: 'quiz',
			type: 'options',
			options: [{
				name: 'Example quiz',
				code: 'test'
			}, {
				name: 'Powerful Owl quiz',
				code: 'powl'
			}, {
				name: 'Glossy Black-Cockatoo quiz',
				code: 'glossy'
			}, {
				name: 'BNB quiz (External)',
				code: 'bnb_external'
			}]
		}, {
			name: 'prioritySiteIds',
			label: 'Priority sites',
			type: 'text',
			multiline: true,
			help: `Optional: Enter a list of priority site IDs (one ID per line).<br><br>
			Each ID should either be a valid grid cell ID, shared site ID or shared site name.<br><br>
			Priority sites will be highlighted on the site booking map.<br><br>
			Note: you can copy and paste an entire column directly from Excel.`
		}, {
			name: 'siteIds',
			label: 'Available sites',
			type: 'text',
			multiline: true,
			help: `Optional: Enter a list of site IDs that volunteers are able to book for this event (one ID per line).<br><br>
			Leave this field blank to allow volunteers to book any site belonging to the program.<br><br>
			Each ID should either be a valid grid cell ID, shared site ID or shared site name.<br><br>
			Note: you can copy and paste an entire column directly from Excel.`,
		}];

		var fields = fieldDefinitions.map(fieldManager.field);
		var fieldsByName = util.arrayToMap(fields, function(f) { return f.field.name; });

		if(event) {
			fields.forEach(f => f.field.load(event));
		}

		self.fieldList(fields);
		self.fields(fieldsByName);
		self.loaded(true);
	}).catch(function(error) {
		// TODO: Indicate error to user
		console.log(error);
	});


	self.submit = function() {
		var json = {};
		self.fieldList().forEach(f => f.field.save(json));

		if(!params.id) {
			json.code = json.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
		}
		json.id = params.id;

		self.submitting(true);

		var p = params.id ? birdata.updateEvent(json) : birdata.createEvent(json);

		p.then(function(result) {
			params.parent.viewEventDetails(params.id || result.id);
		}).catch(function(error) {
			console.log(arguments);
			self.submitError("An unexpected error has occurred.");
		}).finally(function() {
			self.submitting(false);
		});
	}

	self.cancel = function() {
		if(params.id) {
			params.parent.viewEventDetails(params.id);
		} else {
			params.parent.listEvents();
		}
	}

	self.canSubmit = ko.pureComputed(function() {
		if(self.submitting()) {
			return false;
		}
		var fields = self.fieldList();
		if(fields) {
			return fields.filter(f => f.field.validate()).length == 0;
		} else {
			return false;
		}
	})

	// Site selection

	var programObs = ko.observable();
	programs().then(programObs);

	var selectedProgram = ko.pureComputed(function() {
		var p = self.fields().program();
		console.log(p);
		return p && p.code && programObs() && programObs().find(p.code);
	});

	function createSiteDialogModel() {
		var dialogModel = {};

		var pageSize = 10;

		dialogModel.loaded = ko.observable();
		dialogModel.sites = ko.observable();
		dialogModel.searchText = ko.observable("");
		dialogModel.allSitesAvailable = ko.observable(true);
		dialogModel.currentPage = ko.observable(0);

		dialogModel.filteredSites = ko.pureComputed(function() {
			var search = dialogModel.searchText().toLowerCase().trim();
			dialogModel.currentPage(0);
			return dialogModel.sites().filter(site =>
				site.name.toLowerCase().indexOf(search) != -1)
		})

		dialogModel.pageLabel = function() {
			return (dialogModel.currentPage() + 1) + " / " + Math.max(dialogModel.pageCount(), 1);
		}

		dialogModel.canNavigatePage = function(delta) {
			let page = dialogModel.currentPage() + delta;
			return page == clampPage(page)
		}
		dialogModel.navigatePage = function(delta) {
			return function() {
				dialogModel.currentPage(clampPage(dialogModel.currentPage() + delta))
			}
		}

		dialogModel.selectMatchingAvailableLabel = ko.pureComputed(function() {
			return "Select all as available (" + dialogModel.filteredSites().length + ")";
		});
		dialogModel.selectMatchingPriorityLabel = ko.pureComputed(function() {
			return "Select all as priority (" + dialogModel.filteredSites().length + ")";
		});
		dialogModel.selectMatchingAvailable = function() {
			dialogModel.filteredSites().forEach(s => {
				s.available(true);
			});
		}
		dialogModel.selectMatchingPriority = function() {
			dialogModel.filteredSites().forEach(s => {
				s.priority(true);
			});
		}
		dialogModel.clear = function() {
			dialogModel.searchText("");
			dialogModel.sites().forEach(s => {
				s.priority(false);
				s.isAvailable(false);
			});
		}
		dialogModel.anySelected = ko.pureComputed(function() {
			return dialogModel.sites().find(x => x.priority() || x.isAvailable())
		})

		function clampPage(page) {
			return Math.max(Math.min(page, dialogModel.pageCount() - 1), 0)
		}

		function availableSiteIds() {
			if(dialogModel.allSitesAvailable()) {
				return [];
			} else {
				return dialogModel.sites().filter(s => s.available()).map(s => s.id)
			}
		}

		function prioritySiteIds() {
			return dialogModel.sites().filter(s => s.priority()).map(s => s.id);
		}

		function extractLines(str) {
			return (str || "").split(/[\n\r]+/).map(x => x.trim()).filter(x => x.length > 0);
		}

		function loadExistingState() {
			var siteIds = new Set(extractLines(self.fields().siteIds()));
			var prioritySiteIds = new Set(extractLines(self.fields().prioritySiteIds()));

			if(siteIds.size == 0) {
				dialogModel.allSitesAvailable(true);
			} else {
				dialogModel.allSitesAvailable(false);
			}

			dialogModel.sites().forEach(function(site) {
				if(siteIds.has("" + site.id) || siteIds.has(site.name)) {
					site.available(true);
				}
				if(prioritySiteIds.has("" + site.id) || prioritySiteIds.has(site.name)) {
					site.priority(true);
				}
			});
		}

		dialogModel.commit = function() {
			self.fields().siteIds(availableSiteIds().join("\n"));
			self.fields().prioritySiteIds(prioritySiteIds().join("\n"));
			self.siteDialog(null);
		}

		dialogModel.cancel = function() {
			self.siteDialog(null);
		}

		dialogModel.pageCount = ko.pureComputed(function() {
			return Math.ceil(dialogModel.filteredSites().length / pageSize);
		})

		dialogModel.visibleSites = ko.pureComputed(function() {
			var offset = dialogModel.currentPage() * pageSize
			return dialogModel.filteredSites().slice(
				offset, offset + pageSize);
		});

		// load shared sites for selected program
		var program = selectedProgram();
		if(!program) {
			return;
		}

		var layers = program.visibleSharedSiteLayers?.() ?? ["birdata"];
		var sharedSiteLayerPromises = layers.map(layer => birdata.sharedSites({ layer: layer }));
		var sharedSitesPromise = Promise.all(sharedSiteLayerPromises)
			.then(sites => sites.flat(1));

		sharedSitesPromise.then(sites => {
			dialogModel.sites(sites.map(site => {
				let model = {
					id: site.id,
					name: site.name,
					isAvailable: ko.observable(),
					available: ko.pureComputed({
						read: function() {
							return model.isAvailable() || dialogModel.allSitesAvailable()
						},
						write: function(v) {
							model.isAvailable(v)
						}
					}),
					priority: ko.observable()
				}

				model.available.subscribe(function(available) {
					if(!available) {
						model.priority(false);
					}
				});

				model.priority.subscribe(function(priority) {
					if(priority) {
						model.available(true);
					}
				});

				return model
			}))

			loadExistingState();

			dialogModel.loaded(true);
		});

		return dialogModel;
	}

	self.siteDialog = ko.observable();

	self.selectSites = function() {
		self.siteDialog(createSiteDialogModel())
	}

	self.showSelectSites = ko.pureComputed(function() {
		return selectedProgram() && !selectedProgram().mapGrid;
	});
}

ko.components.register('manage-events-edit', {
	viewModel: function(params) {
		return new ManageEventsEditViewModel(params);
	},
	template: require('../../../../templates/admin/events/manage-events-edit.html')
});
